import { ReactNode } from 'react';

import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import type { CapacityPool } from 'features/common/hooks/useEventCapacity';
import { buildTypeColumnProps, ColumnPalette } from './buildTypeColumnProps';
import type { InventoryColumn } from './useTicketInventoryData';
import type { InventoryColumnCardProps } from './InventoryColumnCard';

/**
 * Per-type card for a POOLED ticket tier. It is the standalone card (`buildTypeColumnProps`) with one
 * difference: the issued-donut center tooltip + help spell out BOTH capacities — the tier's own (X)
 * and the shared pool's (Y) — because a pooled tier can sell only as much as the shared pool has left.
 *
 * The donut's "Available" slice is POOL-CAPPED (the sellable count, 0 when the shared pool is full); the
 * tier's own leftover seats the pool blocks render as a neutral "Pool full" slice (NOT a member-coloured
 * wedge — the legacy "Pools" category, bug 2c). The ring still sums to the tier's own capacity, and the
 * dual-capacity tooltip below spells out both caps in words. See docs/pools_business_logic.md §3.
 */
export function buildPooledTypeColumnProps(
	col: InventoryColumn,
	pool: CapacityPool,
	palette: ColumnPalette,
	t: (key: string) => string
): InventoryColumnCardProps {
	const base = buildTypeColumnProps(col, palette, t);

	// Pool usage comes straight from the shared CapacityPool (Lifetime-Σ) — same single source as the pool
	// card and the dashboard/Send tiles; no local recompute.
	const dualCapacityHelp: ReactNode = (
		<PunchupHelpButton
			mainText={`How capacity for "${col.name}" is allocated. It draws from the shared "${pool.name}" pool, so it can sell only as much as the pool has left.`}
			codeText={`"${col.name}" = ${col.issued}/${col.trueCapacity}  (this tier)\n"${pool.name}" pool = ${pool.sold}/${pool.totalAmount}  (${pool.poolRemaining} left, shared)`}
			secondaryText="Available is the pool-capped sellable count (its own remaining, capped by what the shared pool has left). When the pool is full it reads 0, and this tier's own leftover seats show as a grey 'Pool full' slice — present, but not sellable."
		/>
	);

	return {
		...base,
		issuedDonut: {
			...base.issuedDonut,
			centerTooltip: `${col.issued} issued (sold + comped) of ${col.trueCapacity} for this tier · ${pool.poolRemaining} of ${pool.totalAmount} left in the shared "${pool.name}" pool`,
			helpButton: dualCapacityHelp,
		},
	};
}
